// SecurityPage — Security & Backup settings
// Layout:
//   - Top nav: < Back | "Security"
//   - Hero: security score
//   - Sections: Authentication, Backup, Sessions, Encryption
//   - Each item with iOS-style switch or chevron

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Session {
  id: string;
  device: string;
  os: string;
  browser: string;
  ip: string;
  location: string;
  current: boolean;
  lastActive: string;
}

const SESSIONS: Session[] = [
  { id: 's1', device: 'iPhone 15 Pro', os: 'iOS 18.0',  browser: 'FunderPro app',  ip: '203.0.113.42', location: 'San Francisco, US', current: true,  lastActive: 'Now' },
  { id: 's2', device: 'MacBook Pro',   os: 'macOS 15',  browser: 'Safari 17',     ip: '203.0.113.42', location: 'San Francisco, US', current: false, lastActive: '2h ago' },
  { id: 's3', device: 'iPad Air',      os: 'iPadOS 18', browser: 'FunderPro app',  ip: '198.51.100.7', location: 'San Francisco, US', current: false, lastActive: 'Yesterday' },
];

export function SecurityPage() {
  const nav = useNavigate();
  const [twoFA, setTwoFA] = useState(true);
  const [biometric, setBiometric] = useState(true);
  const [emailNotif, setEmailNotif] = useState(true);
  const [smsNotif, setSmsNotif] = useState(false);
  const [autoBackup, setAutoBackup] = useState(true);
  const [encryptBackup, setEncryptBackup] = useState(true);

  // Mock security score
  const score = 85;
  const color = score >= 80 ? '#34C759' : score >= 60 ? '#FF9500' : '#FF3B30';
  const level = score >= 80 ? 'Strong' : score >= 60 ? 'Good' : 'Needs work';

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Security</div>
        <button className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 12l2 2 4-4M5 7h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z" strokeLinecap="round" /></svg>
        </button>
      </div>

      {/* Hero — security score */}
      <div className="mx-4 mt-4">
        <div className="ios26-hero text-center">
          <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Security score</div>
          <div className="relative mx-auto mt-2 h-32 w-32">
            <svg viewBox="0 0 100 100" className="h-32 w-32 -rotate-90">
              <circle cx="50" cy="50" r="42" stroke="#F2F2F7" strokeWidth="8" fill="none" />
              <circle
                cx="50" cy="50" r="42" stroke={color} strokeWidth="8" fill="none"
                strokeDasharray={`${2 * Math.PI * 42}`}
                strokeDashoffset={`${2 * Math.PI * 42 * (1 - score / 100)}`}
                strokeLinecap="round"
                className="transition-all"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-[36px] font-bold tabular-nums" style={{ color }}>{score}</div>
              <div className="text-[10px] uppercase tracking-wider text-tf-text-3">of 100</div>
            </div>
          </div>
          <div className="mt-1 text-[14px] font-bold" style={{ color }}>{level}</div>
        </div>
      </div>

      {/* Authentication */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Authentication</h2>
      <div className="mx-4 ios26-list bg-white">
        <SettingRow icon="🔐" iconBg="#007AFF" title="Two-factor auth" sub="Use an authenticator app" toggle={twoFA} onToggle={setTwoFA} testid="setting-2fa" />
        <SettingRow icon="🛡️" iconBg="#5856D6" title="Biometric login"  sub="Face ID / Touch ID"      toggle={biometric} onToggle={setBiometric} testid="setting-biometric" />
        <SettingRow icon="🔑" iconBg="#FF9500" title="Change password"  sub="Last changed 14 days ago" chevron testid="setting-password" />
        <SettingRow icon="📱" iconBg="#34C759" title="Recovery phone"   sub="+1 (555) 123-4567"        chevron testid="setting-phone" />
      </div>

      {/* Backup */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Backup</h2>
      <div className="mx-4 ios26-list bg-white">
        <SettingRow icon="☁️" iconBg="#34C759" title="Auto backup"      sub="Daily, last 1:23 AM"        toggle={autoBackup}    onToggle={setAutoBackup}    testid="setting-backup" />
        <SettingRow icon="🔒" iconBg="#5856D6" title="Encrypt backups"  sub="AES-256 encryption"        toggle={encryptBackup} onToggle={setEncryptBackup} testid="setting-encrypt" />
        <SettingRow icon="💾" iconBg="#FF9500" title="Download backup"  sub="Get a copy of your data"    chevron testid="setting-download" />
        <SettingRow icon="🔄" iconBg="#007AFF" title="Restore from backup" sub="Upload to restore"       chevron last testid="setting-restore" />
      </div>

      {/* Notifications */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Security notifications</h2>
      <div className="mx-4 ios26-list bg-white">
        <SettingRow icon="📧" iconBg="#007AFF" title="Email alerts" sub="New login, password change, etc." toggle={emailNotif} onToggle={setEmailNotif} testid="setting-email" />
        <SettingRow icon="📲" iconBg="#34C759" title="SMS alerts"   sub="Critical events only"               toggle={smsNotif}   onToggle={setSmsNotif}   last testid="setting-sms" />
      </div>

      {/* Sessions */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Active sessions</h2>
      <div className="mx-4 ios26-list bg-white">
        {SESSIONS.map((s, i) => (
          <div
            key={s.id}
            data-testid={`session-${s.id}`}
            className={['flex items-center gap-3 px-4 py-3', i < SESSIONS.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-tf-blue-tint text-[18px]">
              {s.os.includes('iOS') ? '📱' : s.os.includes('macOS') ? '💻' : '📟'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[14px] font-semibold text-tf-text-1">{s.device}</span>
                {s.current && <span className="rounded-full bg-[#34C759] px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">This device</span>}
              </div>
              <div className="text-[11px] text-tf-text-3">{s.browser} · {s.location}</div>
              <div className="text-[10px] text-tf-text-3">{s.ip} · {s.lastActive}</div>
            </div>
            {!s.current && (
              <button onClick={() => alert('Session terminated')} className="text-tf-loss text-[12px] font-semibold">Terminate</button>
            )}
          </div>
        ))}
      </div>

      <div className="mx-4 mt-4 flex flex-col gap-2">
        <button onClick={() => alert('Sign out of all other devices')} className="flex h-12 w-full items-center justify-center rounded-full bg-[#FF3B30]/10 text-[15px] font-semibold text-tf-loss active:opacity-40">Sign out all other devices</button>
        <button onClick={() => { if (confirm('Delete account permanently? This cannot be undone.')) alert('Account deletion request submitted.'); }} className="flex h-12 w-full items-center justify-center rounded-full bg-white border border-[#FF3B30] text-[15px] font-semibold text-tf-loss active:opacity-40">Delete account</button>
      </div>
    </div>
  );
}

function SettingRow({ icon, iconBg, title, sub, toggle, onToggle, chevron, last, testid }: { icon: string; iconBg: string; title: string; sub: string; toggle?: boolean; onToggle?: (v: boolean) => void; chevron?: boolean; last?: boolean; testid?: string }) {
  return (
    <div data-testid={testid} className={['flex items-center gap-3 px-4 py-3', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <div className="flex h-9 w-9 items-center justify-center rounded-[10px] text-[16px]" style={{ backgroundColor: iconBg + '18' }}>{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-medium text-tf-text-1">{title}</div>
        <div className="text-[12px] text-tf-text-3 truncate">{sub}</div>
      </div>
      {toggle !== undefined && (
        <button
          onClick={() => onToggle?.(!toggle)}
          data-testid={`toggle-${testid}`}
          className={['relative h-7 w-12 rounded-full transition-colors', toggle ? 'bg-[#34C759]' : 'bg-[#E5E5EA]'].join(' ')}
        >
          <div className={['absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-md transition-all', toggle ? 'left-[22px]' : 'left-0.5'].join(' ')} />
        </button>
      )}
      {chevron && <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>}
    </div>
  );
}
